import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useBreadcrumb } from '../../../context/BreadcrumbContext';
import {
  Box,
  Grid,
  Typography,
  IconButton,
  Tooltip,
  FormLabel,
  FormGroup,
  FormControlLabel,
  Checkbox,
  CircularProgress
} from '@mui/material';
import CustomTooltip from '../../../components/common/CustomTooltip';
import ClearIcon from '@mui/icons-material/Clear';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { AppDispatch } from '../../../store';
import FacilityProfileTab from '../../../components/FacilityProfileTab'; // Changed from UserProfileTab
import DocsIcon from '../../../assets/main/docs-icon.svg'
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import DownloadIcon from '@mui/icons-material/Download';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import VerificationIcon from '../../../assets/main/verification-icon.svg'
import { getFileNameFromPath, truncateLast } from '../../../helpers';
import PageCard from '../../../components/PageCard';
import CardTitle from '../../../components/CardTitle'
import ActionPopup from '../../../components/common/ActionPopup';

import {
  BasicDetailItemContainer,
  CustomCard,
  DocsContainer,
  DateContainer,
  DateTextField,
  ButtonGroupContainer,
  VerificationImageContainer
} from './styles';

// redux
import {
  fetchFacilityDetails,
  selectFacilityDetails,
  IUploadedDocument as IFacilityUploadedDocument,
  updateFacilityDocumentDates,
  markFacilityAsViewed,
  acceptRejectFacilityDocument,
  selectAcceptRejectFacilityDocumentLoading,
  selectMarkFacilityAsViewedLoading,
  selectUpdateFacilityDocumentDatesLoading
} from '../../../modules/facility';

import AlertPopup from '../../../components/common/AlertPopup';
import ConfirmationDialog from '../../../components/common/ConfirmationDialog';
import DocumentPreviewPopup from '../../../components/common/DocumentPreviewPopup'; // Import the new component
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CancelIcon from '@mui/icons-material/Cancel';
import apiCall from '../../../utils/api';
import { API_URL } from '../../../utils/env';


interface IFacilityDetailsUploadedDocument extends IFacilityUploadedDocument {
  issuedDate: dayjs.Dayjs | null;
  expiresDate: dayjs.Dayjs | null;
  is_accepted: boolean;
  is_rejected: boolean;
}

const FacilityOnboardPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { setPath, setIsShow } = useBreadcrumb();

  const dispatch: AppDispatch = useDispatch();
  const facilityDetails = useSelector(selectFacilityDetails);
  const documentStatusUpdateLoading = useSelector(selectAcceptRejectFacilityDocumentLoading);
  const markFacilityAsViewedLoading = useSelector(selectMarkFacilityAsViewedLoading);
  const updateFacilityDocumentDatesLoading = useSelector(selectUpdateFacilityDocumentDatesLoading);

  const [uploadedDocuments, setUploadedDocuments] = useState<IFacilityDetailsUploadedDocument[]>([])
  const [facilityId, setFacilityId] = useState<number>(0)
  const [alert, setAlert] = useState<{ message: string, severity: 'success' | 'error' } | null>(null);
  const [facilityStatusCheckbox, setFacilityStatusCheckbox] = useState<boolean>(false);

  const [confirmation, setConfirmation] = useState<{ open: boolean; title: string; message: string; onConfirm: (() => void) | null }>({ open: false, title: '', message: '', onConfirm: null });


  const [showPreviewPopup, setShowPreviewPopup] = useState<boolean>(false); // State for DocumentPreviewPopup visibility
  const [previewFileUrl, setPreviewFileUrl] = useState<string>(''); // State for the URL of the file to preview
  const [previewFileType, setPreviewFileType] = useState<'image' | 'pdf' | 'other'>('other'); // State for the type of file to preview
  const [previewFileName, setPreviewFileName] = useState<string>(''); // State for the name of the file to preview

  const [showRejectPopup, setShowRejectPopup] = useState<boolean>(false); // State for ActionPopup visibility
  const [documentToReject, setDocumentToReject] = useState<IFacilityDetailsUploadedDocument | null>(null); // State to hold document data for rejection
  const [showAcceptPopup, setShowAcceptPopup] = useState<boolean>(false); // State for ActionPopup visibility for accept
  const [documentToAccept, setDocumentToAccept] = useState<IFacilityDetailsUploadedDocument | null>(null); // State to hold document data for accept

  useEffect(() => {
    setPath([
      { title: 'Management', path: '/' },
      { title: 'Facility', path: '/facilities' }, // Changed from /workers
      { title: 'Details', path: '' }, // Changed from Onboarding
    ]);
    setIsShow(true)
  }, [setPath, setIsShow]);

  useEffect(() => {
    try {
      if (id) {
        const facilityId = parseInt(id)
        setFacilityId(facilityId)
        dispatch(fetchFacilityDetails(facilityId) as any);
      }
    } catch (err) {
      console.log(err);
    }
  }, [dispatch, id])

  useEffect(() => {
    if (facilityDetails) {

      if (facilityDetails.uploaded_documents) {
        const documents = facilityDetails.uploaded_documents.map(doc => ({
          ...doc,
          issuedDate: doc.document_issued_date ? dayjs(doc.document_issued_date) : null,
          expiresDate: doc.document_expiring_date ? dayjs(doc.document_expiring_date) : null,
        }));
        setUploadedDocuments(documents);
      } else {
        setUploadedDocuments([]);
      }


      setFacilityStatusCheckbox(facilityDetails.viewed)


    }
  }, [facilityDetails])



  const handleDocumentDateChange = async (index: number, field: 'issuedDate' | 'expiresDate', newValue: dayjs.Dayjs | null) => {
    const updatedDocuments = [...uploadedDocuments];
    updatedDocuments[index] = {
      ...updatedDocuments[index],
      [field]: newValue,
    };
    setUploadedDocuments(updatedDocuments);

    const documentToUpdate = updatedDocuments[index];
    if (documentToUpdate.document_id) {
      const payload: { documentId: number; document_issued_date?: string; document_expiring_date?: string } = {
        documentId: documentToUpdate.document_id,
      };

      if (field === 'issuedDate') {
        payload.document_issued_date = newValue ? newValue.format('YYYY-MM-DD') : undefined;
      } else if (field === 'expiresDate') {
        payload.document_expiring_date = newValue ? newValue.format('YYYY-MM-DD') : undefined;
      }

      const result = await dispatch(updateFacilityDocumentDates(payload) as any);

      if (updateFacilityDocumentDates.fulfilled.match(result)) {
        setAlert({ message: 'Document dates updated successfully!', severity: 'success' });
        dispatch(fetchFacilityDetails(facilityId) as any); // Refresh facility details
      } else {
        setAlert({ message: (result.payload as string) || 'Failed to update document dates.', severity: 'error' });
      }
    }
  };

  // Removed handleSave and worker-specific update functions

  interface BasicDetailItemProps {
    title: string;
    value: string | undefined | null;
  }

  const BasicDetailItem = ({ title, value }: BasicDetailItemProps) => (
    <BasicDetailItemContainer>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mb: 0.5, fontWeight: 600 }}
      >
        {title}
      </Typography>
      <Typography
        variant="body1"
        color="text.primary"
        fontWeight="medium"
      >
        {value}
      </Typography>
    </BasicDetailItemContainer>
  )

  const getFileType = (url: string): 'image' | 'pdf' | 'other' => {
    const extension = url.split('.').pop()?.toLowerCase();
    if (extension && ['jpg', 'jpeg', 'png', 'gif'].includes(extension)) {
      return 'image';
    }
    if (extension === 'pdf') {
      return 'pdf';
    }
    return 'other';
  };

  const handleDownload = async (url: string, fileName: string) => {
    try {
      let blob: Blob;
      if (url.startsWith('http') || url.startsWith('blob:') || url.startsWith('data:')) {
        const response = await fetch(url);
        if (!response.ok) throw new Error('Network response was not ok');
        blob = await response.blob();
      } else {
        const response = await apiCall<Blob>(
          'get',
          `/api/fetch-s3-file/?file_path=${encodeURIComponent(url)}&file_format=blob`,
          undefined,
          undefined,
          'blob'
        );
        blob = response.data;
      }

      if (!(blob instanceof Blob)) {
        throw new Error('Failed to get a valid file');
      }

      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Download failed:', error);
      const fallbackUrl = url.startsWith('http') ? url : `${API_URL}${url}`;
      window.open(fallbackUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <Box>
      {alert && <AlertPopup message={alert.message} severity={alert.severity} open={!!alert} onClose={() => setAlert(null)} />}
      {facilityDetails && // Changed from workerDetails
        <PageCard padding={4} backgroundRequired={true}>
          <FacilityProfileTab
            facilityName={facilityDetails.facility_name}
            id={facilityDetails.facility_id}
            status={facilityDetails.status}
          />
        </PageCard>
      }
      <PageCard padding={2} backgroundRequired={true}>
        <CustomCard>
          <CardTitle title='Basic Details' />
          <Grid container spacing={2}>
            <Grid size={3}>
              <BasicDetailItem
                title='Facility Name'
                value={facilityDetails?.facility_name} // Changed from workerDetails?.user.first_name
              />
              <BasicDetailItem
                title='Contact Person'
                value={facilityDetails?.contact_person} // Changed from workerDetails?.user.phone_number
              />
            </Grid>
            <Grid size={3}>
              <BasicDetailItem
                title='Type'
                value={facilityDetails?.facility_type_name} // Changed from workerDetails?.user.last_name
              />
              <BasicDetailItem
                title='Designation'
                value={facilityDetails?.contact_person_designation} // Changed from workerDetails?.user.email
              />
            </Grid>
            <Grid size={3}>
              <BasicDetailItem
                title='ABN Number'
                value={facilityDetails?.abn_number} // Changed from workerDetails?.gender
              />
              <BasicDetailItem
                title='Phone Number'
                value={facilityDetails?.phone_number} // Changed from workerDetails?.user.first_name + " " + workerDetails?.user.last_name
              />
            </Grid>
            <Grid size={3}>
              <BasicDetailItem
                title='Email Address'
                value={facilityDetails?.email} // Changed from workerDetails?.gender
              />
              <BasicDetailItem
                title='Address'
                value={facilityDetails?.address} // Changed from dayjs(workerDetails?.date_of_birth).format('DD/MM/YYYY')
              />
            </Grid>
          </Grid>
        </CustomCard>

        <CustomCard>
          <CardTitle title='Documents Uploaded' />
          {/* Removed workerDetails?.police_verification block */}

          {uploadedDocuments &&
            uploadedDocuments.length > 0 &&
            uploadedDocuments.map((document, index) => (
              <DocsContainer key={document.document_id}> {/* Added key */}
                <Grid container>
                  <Grid size={6}>
                    <Grid container spacing={2} wrap="nowrap">
                      <Grid sx={{ marginTop: 1 }}>
                        <img src={DocsIcon} alt="Document Icon" />
                      </Grid>
                      <Grid>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {document.document_name}
                        </Typography>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography
                            variant='subtitle2'
                            fontWeight="body2"
                            title={getFileNameFromPath(document.document_url)}
                            sx={{ cursor: 'pointer' }}
                          >
                            {truncateLast(getFileNameFromPath(document.document_url))}
                          </Typography>
                          {document.is_accepted &&
                            <CustomTooltip
                              title={document.remarks}
                              arrow
                              placement="top"
                            >
                              <CheckCircleRoundedIcon color='primary' />
                            </CustomTooltip>
                          }
                          {document.is_rejected &&
                            <CustomTooltip
                              title={document.remarks}
                              arrow
                              placement="top"
                            >
                              <CancelIcon color='error' />
                            </CustomTooltip>
                          }
                        </Box>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid size={6}>
                    <Grid container>
                      <Grid size={6}>
                        <DateContainer>
                          <FormGroup>
                            <FormLabel>
                              Issued:
                            </FormLabel>
                            <Box sx={{ position: 'relative' }}>
                              <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker format="DD/MM/YYYY"
                                  enableAccessibleFieldDOMStructure={false}
                                  value={document.issuedDate}
                                  onChange={(newValue) => handleDocumentDateChange(index, 'issuedDate', newValue)}
                                  slots={{ textField: DateTextField }}
                                  slotProps={{
                                    textField: {
                                      variant: 'outlined',
                                      size: 'small',
                                    },
                                    popper: {
                                      disablePortal: true,
                                      sx: {
                                        position: 'absolute',
                                        top: '100%',
                                        left: '0',
                                        transform: 'none !important',
                                        zIndex: 1300,
                                      }
                                    }
                                  }}
                                  disabled={updateFacilityDocumentDatesLoading}
                                />
                              </LocalizationProvider>
                            </Box>
                          </FormGroup>
                          <FormGroup>
                            <Typography variant="body2" sx={{ mr: 1, ml: 1, mt: 2 }}>
                              to
                            </Typography>
                          </FormGroup>
                          <FormGroup>
                            <FormLabel>
                              Expires:
                            </FormLabel>
                            <Box sx={{ position: 'relative' }}>
                              <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker format="DD/MM/YYYY"
                                  enableAccessibleFieldDOMStructure={false}
                                  value={document.expiresDate}
                                  onChange={(newValue) => handleDocumentDateChange(index, 'expiresDate', newValue)}
                                  slots={{ textField: DateTextField }}
                                  slotProps={{
                                    textField: {
                                      variant: 'outlined',
                                      size: 'small',
                                    },
                                    popper: {
                                      disablePortal: true,
                                      sx: {
                                        position: 'absolute',
                                        top: '100%',
                                        left: '0',
                                        transform: 'none !important',
                                        zIndex: 1300,
                                      }
                                    }
                                  }}
                                  disabled={updateFacilityDocumentDatesLoading}
                                />
                              </LocalizationProvider>
                            </Box>
                          </FormGroup>
                          {updateFacilityDocumentDatesLoading && <CircularProgress size={20} sx={{ ml: 2, mt: 2 }} />}
                        </DateContainer>
                      </Grid>
                      <Grid size={6}>
                        <Box flexShrink={0} textAlign="right">
                          <Tooltip title="Preview">
                            <IconButton
                              onClick={() => {
                                const fullUrl = document.document_url;
                                setPreviewFileUrl(fullUrl);
                                setPreviewFileType(getFileType(fullUrl));
                                setPreviewFileName(document.document_name);
                                setShowPreviewPopup(true);
                              }}
                            >
                              <RemoveRedEyeIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Download">
                            <IconButton
                              onClick={() => {
                                const fullUrl = document.document_url;
                                handleDownload(fullUrl, getFileNameFromPath(fullUrl));
                              }}
                              color="primary"
                            >
                              <DownloadIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Accept">
                            <span>
                              <IconButton
                                color="success"
                                onClick={() => {
                                  setDocumentToAccept(document);
                                  setShowAcceptPopup(true);
                                }}
                                disabled={documentStatusUpdateLoading || document.is_accepted}
                              >
                                <CheckCircleOutlineIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Reject">
                            <span>
                              <IconButton
                                color="error"
                                onClick={() => {
                                  setDocumentToReject(document);
                                  setShowRejectPopup(true);
                                }}
                                disabled={documentStatusUpdateLoading || document.is_rejected}
                              >
                                <ClearIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </DocsContainer>
            ))
          }


        </CustomCard>
      </PageCard>

      <PageCard padding={2} backgroundRequired={true}>
        <Grid container>
          <Grid size={6}>
            <Grid container spacing={2}>
              <VerificationImageContainer size={1}>
                <img src={VerificationIcon} alt="Verification Icon" />
              </VerificationImageContainer>
              <Grid size={11}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Verification Action
                </Typography>
                <Typography variant='subtitle2' fontWeight="body2">
                  Confirm the facility's eligibility and finalize their status {/* Changed from worker's */}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid size={6} >
            <Box flexShrink={0} textAlign="right">
              <ButtonGroupContainer>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={facilityStatusCheckbox === true}
                      onChange={(event) => {
                        const newStatus = event.target.checked ? true : false;

                        setConfirmation({
                          open: true,
                          title: `Confirm ${event.target.checked ? 'Verification' : 'Un-verification'}`,
                          message: `Are you sure, you want to ${event.target.checked ? 'verify' : 'un-verify'} this facility?`,
                          onConfirm: () => {
                            dispatch(markFacilityAsViewed(facilityId) as any);
                            setFacilityStatusCheckbox(newStatus);
                            setConfirmation({ open: false, title: '', message: '', onConfirm: null });
                          },
                        });
                      }}
                      disabled={markFacilityAsViewedLoading}
                    />
                  }
                  label="Verified"
                />
                {markFacilityAsViewedLoading && <CircularProgress size={20} />}
              </ButtonGroupContainer>
            </Box>
          </Grid>
        </Grid>
      </PageCard>
      <ConfirmationDialog
        open={confirmation.open}
        onClose={() => setConfirmation({ open: false, title: '', message: '', onConfirm: null })}
        onConfirm={confirmation.onConfirm || (() => { })}
        title={confirmation.title}
        message={confirmation.message}
      />
      <ActionPopup
        open={showRejectPopup}
        onClose={() => {
          setShowRejectPopup(false);
          setDocumentToReject(null);
        }}
        onSubmit={async (remark) => {
          if (!documentToReject || !facilityId) return;

          const result = await dispatch(acceptRejectFacilityDocument({
            documentId: documentToReject.document_id,
            action: 'reject',
            remarks: remark ?? '',
          }) as any);

          if (acceptRejectFacilityDocument.fulfilled.match(result)) {
            setAlert({ message: result.payload?.message || 'Document rejected successfully!', severity: 'success' });
            dispatch(fetchFacilityDetails(facilityId) as any); // Refresh facility details
            setShowRejectPopup(false);
            setDocumentToReject(null);
          } else {
            setAlert({ message: (result.payload as string) || 'Failed to reject document.', severity: 'error' });
          }
        }}
        title="Reject Document"
        message={`Are you sure you want to reject the document "${documentToReject?.document_name}"? Please provide a remark.`}
        submitButtonText="Reject"
        textFieldLabel="Rejection Remark"
        textFieldPlaceholder="Enter reason for rejection"
        loading={documentStatusUpdateLoading}
        required={true}
      />
      <ActionPopup
        open={showAcceptPopup}
        onClose={() => {
          setShowAcceptPopup(false);
          setDocumentToAccept(null);
        }}
        onSubmit={async (remark) => {
          if (!documentToAccept || !facilityId) return;

          const result = await dispatch(acceptRejectFacilityDocument({
            documentId: documentToAccept.document_id,
            action: 'accept',
            remarks: remark || '',
            document_issued_date: documentToAccept.issuedDate ? documentToAccept.issuedDate.format('YYYY-MM-DD') : undefined,
            document_expiring_date: documentToAccept.expiresDate ? documentToAccept.expiresDate.format('YYYY-MM-DD') : undefined,
          }) as any);

          if (acceptRejectFacilityDocument.fulfilled.match(result)) {
            setAlert({ message: result.payload?.message || 'Document accepted successfully!', severity: 'success' });
            dispatch(fetchFacilityDetails(facilityId) as any);
            setShowAcceptPopup(false);
            setDocumentToAccept(null);
          } else {
            setAlert({ message: (result.payload as string) || 'Failed to accept document.', severity: 'error' });
          }
        }}
        title="Accept Document"
        message={`Are you sure you want to accept the document "${documentToAccept?.document_name}"?`}
        submitButtonText="Accept"
        loading={documentStatusUpdateLoading}
        textFieldLabel="Acceptance Remark"
        textFieldPlaceholder="Enter remark for acceptance"
        required={false}
      />
      <DocumentPreviewPopup
        open={showPreviewPopup}
        onClose={() => setShowPreviewPopup(false)}
        fileUrl={previewFileUrl}
        fileType={previewFileType}
        fileName={previewFileName}
      />
    </Box>
  );
};

export default FacilityOnboardPage;